import React, { useState, useEffect } from 'react';
import { 
  Eye, MousePointerClick, BarChart3, Image as ImageIcon, Calendar, 
  ExternalLink, RefreshCw, AlertCircle, Plus, DollarSign, Flame, 
  Clock, CheckCircle, XCircle, Upload, X 
} from 'lucide-react';
import { advertisementService } from '../../../services/advertisementService';
import { imageUploadService } from '../../../utils/imageUploadService';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

export default function SellerBannerAdsTab({ sellerAccount, user }) {
  const [banners, setBanners] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);

  // New Ad Form State
  const [newAd, setNewAd] = useState({
    title: '',
    description: '',
    backgroundImageUrl: '',
    location: 'catalog_inline',
    adWidth: 'half',
    targetLink: '',
    actionButtonText: 'Explore Now',
    campaignBudget: 50.00,
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const sellerId = sellerAccount?.id;

  const loadBannerData = async () => {
    try {
      setLoading(true);
      setError('');

      let activeSellerId = sellerId;
      if (!activeSellerId && user?.id) {
        const { data: sAccount } = await supabase
          .from('seller_accounts')
          .select('id')
          .eq('user_id', user.id)
          .single();
        activeSellerId = sAccount?.id;
      }

      if (!activeSellerId) {
        setBanners([]);
        setLoading(false);
        return;
      }

      const sellerBanners = await advertisementService.getBannersBySellerId(activeSellerId);
      setBanners(sellerBanners || []);

      // Fetch analytics counts for each banner
      if (sellerBanners && sellerBanners.length > 0) {
        const statsMap = {};
        await Promise.all(
          sellerBanners.map(async (b) => {
            const stats = await advertisementService.getAnalytics(b.id);
            statsMap[b.id] = stats;
          })
        );
        setAnalytics(statsMap);
      }
    } catch (err) {
      console.error('Failed to load seller banner advertising data:', err);
      setError('Unable to load banner advertising data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBannerData();
  }, [sellerId, user?.id]);

  // Aggregate Metrics & Burn Calculations
  const calculateBannerBurn = (banner, stats) => {
    const cpmRate = banner.cpmRate || 2.50;
    const cpcRate = banner.cpcRate || 0.25;
    const views = stats?.views || 0;
    const clicks = stats?.clicks || 0;
    return (views / 1000 * cpmRate) + (clicks * cpcRate);
  };

  const totalBanners = banners.length;
  const activeBanners = banners.filter(b => b.isActive).length;
  const pendingBanners = banners.filter(b => b.approvalStatus === 'pending_approval').length;
  
  const totalBudget = banners.reduce((sum, b) => sum + (parseFloat(b.campaignBudget) || 0), 0);
  const totalBurn = banners.reduce((sum, b) => sum + calculateBannerBurn(b, analytics[b.id]), 0);
  const remainingBudget = Math.max(0, totalBudget - totalBurn);

  const totalImpressions = banners.reduce((sum, b) => sum + (analytics[b.id]?.views || 0), 0);
  const totalClicks = banners.reduce((sum, b) => sum + (analytics[b.id]?.clicks || 0), 0);
  const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';

  // Handle Image Selection for Ad Submission
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Submit Ad for Approval
  const handleSubmitAd = async (e) => {
    e.preventDefault();
    if (!newAd.title.trim()) {
      alert('Please provide a title for the ad.');
      return;
    }

    try {
      setSubmitting(true);
      let imageUrl = newAd.backgroundImageUrl;

      // Upload image if file selected
      if (selectedImageFile) {
        setUploadProgress(true);
        const folder = `banners/${sellerId || user?.id || 'seller'}`;
        const uploadedPaths = await imageUploadService.uploadImages([selectedImageFile], folder, 'storefront-banners');
        if (uploadedPaths && uploadedPaths.length > 0) {
          imageUrl = imageUploadService.getPublicUrl(uploadedPaths[0], 'storefront-banners');
        }
      }

      if (!imageUrl) {
        alert('Please upload an image or provide an image URL for the banner.');
        setSubmitting(false);
        setUploadProgress(false);
        return;
      }

      let activeSellerId = sellerId;
      if (!activeSellerId && user?.id) {
        const { data: sAccount } = await supabase
          .from('seller_accounts')
          .select('id')
          .eq('user_id', user.id)
          .single();
        activeSellerId = sAccount?.id;
      }

      await advertisementService.submitAdForApproval({
        sellerId: activeSellerId,
        title: newAd.title,
        description: newAd.description,
        backgroundImageUrl: imageUrl,
        location: newAd.location,
        adWidth: newAd.adWidth,
        targetLink: newAd.targetLink || null,
        actionButtonText: newAd.actionButtonText || 'Explore Now',
        campaignBudget: parseFloat(newAd.campaignBudget) || 0,
        startDate: newAd.startDate ? new Date(newAd.startDate).toISOString() : new Date().toISOString(),
        endDate: newAd.endDate ? new Date(newAd.endDate).toISOString() : null
      });

      alert('Ad campaign submitted successfully! It is now pending administrator review.');
      setShowSubmitModal(false);
      // Reset form
      setNewAd({
        title: '',
        description: '',
        backgroundImageUrl: '',
        location: 'catalog_inline',
        adWidth: 'half',
        targetLink: '',
        actionButtonText: 'Explore Now',
        campaignBudget: 50.00,
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
      });
      setSelectedImageFile(null);
      setImagePreview(null);
      await loadBannerData();
    } catch (err) {
      console.error('Error submitting ad for approval:', err);
      alert('Failed to submit ad: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
      setUploadProgress(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-3">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground font-medium">Loading banner advertising metrics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertCircle size={20} className="shrink-0 text-red-500" />
          <p className="text-sm font-medium">{error}</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadBannerData} className="text-xs">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      {/* Header and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border">
        <div>
          <h2 className="text-xl font-black text-foreground tracking-tight">Banner Advertising & Impressions</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Monitor real-time campaign budgets, current burn spend, impression counts, and submit new promotional ads for approval.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowSubmitModal(true)}
            className="text-xs font-bold gap-1.5 shadow-sm"
          >
            <Plus size={14} />
            Submit Ad for Approval
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadBannerData}
            disabled={loading}
            className="text-xs font-semibold gap-1.5"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Campaign Financials & Impressions KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Campaign Budget */}
        <div className="bg-card border border-border/80 rounded-xl p-5 shadow-xs flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center shrink-0">
            <DollarSign size={24} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Campaign Budget</p>
            <h3 className="text-2xl font-black text-foreground mt-0.5">${totalBudget.toFixed(2)}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Total authorized budget</p>
          </div>
        </div>

        {/* Current Burn */}
        <div className="bg-card border border-border/80 rounded-xl p-5 shadow-xs flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center shrink-0">
            <Flame size={24} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Current Burn</p>
            <h3 className="text-2xl font-black text-foreground mt-0.5">${totalBurn.toFixed(2)}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              ${remainingBudget.toFixed(2)} remaining balance
            </p>
          </div>
        </div>

        {/* Total Impressions */}
        <div className="bg-card border border-border/80 rounded-xl p-5 shadow-xs flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
            <Eye size={24} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Impressions</p>
            <h3 className="text-2xl font-black text-foreground mt-0.5">{totalImpressions.toLocaleString()}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">{totalClicks.toLocaleString()} clicks ({avgCtr}% CTR)</p>
          </div>
        </div>

        {/* Campaign Ads / Pending Approval */}
        <div className="bg-card border border-border/80 rounded-xl p-5 shadow-xs flex items-center space-x-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
            <ImageIcon size={24} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Campaign Ads</p>
            <h3 className="text-2xl font-black text-foreground mt-0.5">{activeBanners} Active</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {pendingBanners > 0 ? `${pendingBanners} pending approval` : `${totalBanners} total campaigns`}
            </p>
          </div>
        </div>
      </div>

      {/* Banner Ads List */}
      <div className="bg-card border border-border/80 rounded-xl overflow-hidden shadow-xs">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">Your Assigned & Submitted Campaigns</h3>
          <span className="text-xs text-muted-foreground font-medium">{banners.length} {banners.length === 1 ? 'Campaign' : 'Campaigns'} Total</span>
        </div>

        {banners.length === 0 ? (
          <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
              <ImageIcon size={28} />
            </div>
            <h4 className="text-base font-bold text-foreground">No Banner Campaigns Yet</h4>
            <p className="text-xs text-muted-foreground max-w-md leading-relaxed">
              You haven't submitted any banner advertising campaigns yet. Click "Submit Ad for Approval" above to launch a catalog inline banner or homepage carousel placement.
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowSubmitModal(true)}
              className="text-xs font-bold gap-1 mt-2"
            >
              <Plus size={14} />
              Submit Your First Ad
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border overflow-x-auto">
            {banners.map((banner) => {
              const stats = analytics[banner.id] || { views: 0, clicks: 0 };
              const burn = calculateBannerBurn(banner, stats);
              const budget = parseFloat(banner.campaignBudget) || 0;
              const remaining = Math.max(0, budget - burn);
              const burnPercent = budget > 0 ? Math.min(100, (burn / budget) * 100).toFixed(1) : 0;
              const ctr = stats.views > 0 ? ((stats.clicks / stats.views) * 100).toFixed(2) : '0.00';
              const locationLabel = banner.location === 'catalog_inline' ? 'Catalog Inline' : 'Homepage Carousel';
              const slotWidthLabel = (banner.adWidth === 'half' || banner.ad_width === 'half') ? 'Half-Width' : 'Full-Width';

              const approvalStatus = banner.approvalStatus || (banner.isActive ? 'approved' : 'pending_approval');

              return (
                <div key={banner.id} className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-5 hover:bg-muted/30 transition-colors">
                  {/* Banner Image & Info */}
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="h-20 w-32 flex-shrink-0 bg-muted rounded-lg overflow-hidden border border-border relative">
                      {banner.backgroundImageUrl ? (
                        <img 
                          src={banner.backgroundImageUrl} 
                          alt={banner.title || 'Banner'} 
                          className="h-full w-full object-cover" 
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-[10px] text-muted-foreground">No Image</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-sm font-bold text-foreground truncate">{banner.title || 'Untitled Banner'}</h4>
                        
                        {/* Status Badges */}
                        {approvalStatus === 'approved' ? (
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${banner.isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                            {banner.isActive ? 'Active' : 'Inactive'}
                          </span>
                        ) : approvalStatus === 'pending_approval' ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                            <Clock size={10} />
                            Pending Approval
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                            <XCircle size={10} />
                            Rejected
                          </span>
                        )}

                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          {locationLabel}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">
                          {slotWidthLabel}
                        </span>
                      </div>
                      
                      {banner.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{banner.description}</p>
                      )}

                      {banner.rejectionReason && (
                        <p className="text-xs text-rose-600 mt-1 font-medium bg-rose-50 p-1.5 rounded border border-rose-100">
                          Feedback: {banner.rejectionReason}
                        </p>
                      )}

                      <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar size={12} />
                          <span>
                            {banner.startDate ? new Date(banner.startDate).toLocaleDateString() : 'Now'}
                            {' — '}
                            {banner.endDate ? new Date(banner.endDate).toLocaleDateString() : 'Continuous'}
                          </span>
                        </div>
                        {banner.targetLink && (
                          <a 
                            href={banner.targetLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-1 text-primary hover:underline font-medium"
                          >
                            <span>Link</span>
                            <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Budget & Current Burn */}
                  <div className="bg-muted/40 rounded-xl p-3 border border-border/60 min-w-[210px] flex flex-col justify-center">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground font-semibold flex items-center gap-1">
                        <Flame size={12} className="text-rose-500" />
                        Burn Spend:
                      </span>
                      <span className="font-bold text-foreground">${burn.toFixed(2)}</span>
                    </div>

                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-muted-foreground font-semibold flex items-center gap-1">
                        <DollarSign size={12} className="text-indigo-500" />
                        Authorized Budget:
                      </span>
                      <span className="font-bold text-foreground">
                        {budget > 0 ? `$${budget.toFixed(2)}` : 'Unlimited'}
                      </span>
                    </div>

                    {budget > 0 && (
                      <div className="space-y-1">
                        <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              burn >= budget ? 'bg-rose-500' : (burn / budget > 0.8 ? 'bg-amber-500' : 'bg-primary')
                            }`}
                            style={{ width: `${burnPercent}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-[10px] text-muted-foreground font-medium">
                          <span>{burnPercent}% burned</span>
                          <span>${remaining.toFixed(2)} remaining</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Impressions & Engagement */}
                  <div className="flex items-center gap-5 self-start lg:self-center border-t lg:border-t-0 lg:border-l border-border pt-3 lg:pt-0 lg:pl-6 shrink-0">
                    <div className="text-center min-w-[70px]">
                      <div className="flex items-center justify-center gap-1 text-blue-600 font-black text-base">
                        <Eye size={14} />
                        <span>{stats.views.toLocaleString()}</span>
                      </div>
                      <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mt-0.5">Impressions</div>
                    </div>

                    <div className="text-center min-w-[60px]">
                      <div className="flex items-center justify-center gap-1 text-emerald-600 font-black text-base">
                        <MousePointerClick size={14} />
                        <span>{stats.clicks.toLocaleString()}</span>
                      </div>
                      <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mt-0.5">Clicks</div>
                    </div>

                    <div className="text-center min-w-[60px]">
                      <div className="text-purple-600 font-black text-base">{ctr}%</div>
                      <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mt-0.5">CTR</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal: Submit Ad For Approval */}
      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-xl overflow-hidden my-8">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-foreground">Submit Banner Ad for Approval</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Design your ad and submit for administrator review. Approved ads run automatically across the platform.
                </p>
              </div>
              <button 
                type="button" 
                onClick={() => setShowSubmitModal(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitAd} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                  Headline Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Summer Drone Parts Clearance"
                  value={newAd.title}
                  onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                  Description / Subtext
                </label>
                <textarea
                  rows={2}
                  placeholder="e.g. Save 20% on all high-torque precision motors this week only."
                  value={newAd.description}
                  onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    Placement Location
                  </label>
                  <select
                    value={newAd.location}
                    onChange={(e) => setNewAd({ ...newAd, location: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="catalog_inline">Catalog Inline Banner (Recommended)</option>
                    <option value="homepage_carousel">Homepage Carousel Slide</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    Format / Width
                  </label>
                  <select
                    value={newAd.adWidth}
                    onChange={(e) => setNewAd({ ...newAd, adWidth: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="half">Half-Width (Dual Split - 1584x672)</option>
                    <option value="full">Full-Width (1920x670)</option>
                  </select>
                </div>
              </div>

              {/* Banner Image Upload */}
              <div>
                <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                  Banner Image *
                </label>
                <div className="mt-1 flex items-center gap-4">
                  <label className="cursor-pointer px-4 py-2 bg-muted hover:bg-muted/80 text-foreground text-xs font-bold rounded-lg border border-border flex items-center gap-2">
                    <Upload size={14} />
                    <span>Upload Image</span>
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleImageChange}
                      className="hidden" 
                    />
                  </label>
                  <span className="text-xs text-muted-foreground">Or paste image URL below:</span>
                </div>

                <input
                  type="url"
                  placeholder="https://example.com/ad-banner.jpg"
                  value={newAd.backgroundImageUrl}
                  onChange={(e) => {
                    setNewAd({ ...newAd, backgroundImageUrl: e.target.value });
                    setImagePreview(e.target.value);
                  }}
                  className="w-full mt-2 px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />

                {imagePreview && (
                  <div className="mt-2 h-24 w-full rounded-lg overflow-hidden border border-border relative bg-muted">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Campaign Budget Amount & Target Link */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    Campaign Budget Amount ($ USD) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">$</span>
                    <input
                      type="number"
                      min="5"
                      step="5"
                      required
                      placeholder="50.00"
                      value={newAd.campaignBudget}
                      onChange={(e) => setNewAd({ ...newAd, campaignBudget: e.target.value })}
                      className="w-full pl-7 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Delivery pauses once burn reaches this budget.</p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    Target Link / Destination
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. /storefront/my-shop or /model-catalog"
                    value={newAd.targetLink}
                    onChange={(e) => setNewAd({ ...newAd, targetLink: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">Where buyers go when clicking your ad.</p>
                </div>
              </div>

              {/* Start & End Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newAd.startDate}
                    onChange={(e) => setNewAd({ ...newAd, startDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-foreground mb-1 uppercase tracking-wider">
                    End Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={newAd.endDate}
                    onChange={(e) => setNewAd({ ...newAd, endDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-end gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowSubmitModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="sm" 
                  disabled={submitting}
                  className="font-bold gap-1.5"
                >
                  {submitting ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
